import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatCard, PageHeader, Loading, StatusBadge, moneyShort, money } from "@/components/common";
import { Card } from "@/components/ui/card";
import {
  Target, TrendingUp, Wallet, Receipt, LifeBuoy, Percent, ArrowUpRight, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const COLORS = ["#0066FF", "#00C4FF", "#8B5CF6", "#10B981", "#F59E0B", "#F97316", "#EF4444"];

export default function Dashboard() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading || !data) return <div className="space-y-6"><PageHeader title="Dashboard" /><Loading rows={6} /></div>;
  const k = data.kpis;

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle={`${tenant?.name} · ${tenant?.subscription_status === "trial" ? "Free trial" : "Active"} · ${tenant?.plan} plan`}
        testid="dashboard-header"
      >
        <Button variant="outline" className="rounded-full" onClick={() => navigate("/app/war-room")} data-testid="dashboard-warroom-btn">
          <Zap className="mr-1.5 h-4 w-4 text-primary" /> Open War Room
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={Target} label="New Leads" value={k.new_leads} sub={`${k.total_leads} total`} tone="primary" index={0} testid="kpi-new-leads" />
        <StatCard icon={TrendingUp} label="Open Pipeline" value={moneyShort(k.open_pipeline)} sub={`${money(k.won_value)} won`} tone="info" index={1} testid="kpi-pipeline" />
        <StatCard icon={Wallet} label="Outstanding" value={moneyShort(k.outstanding)} sub={`${moneyShort(k.collected)} collected`} tone="warning" index={2} testid="kpi-outstanding" />
        <StatCard icon={Percent} label="Conversion" value={`${k.conversion}%`} sub="won / closed" tone="success" index={3} testid="kpi-conversion" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold">Pipeline by stage</h3>
            <span className="text-xs text-muted-foreground">{money(k.open_pipeline)} open</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.pipeline_by_stage}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#0066FF" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-base font-semibold">Leads by source</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.leads_by_source} dataKey="count" nameKey="source" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {data.leads_by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1.5">
            {data.leads_by_source.map((s, i) => (
              <div key={s.source} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.source}</span>
                <span className="font-medium">{s.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold">Recent leads</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/crm")} data-testid="view-all-leads-btn">
              View all <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="divide-y divide-border">
            {data.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{l.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{l.company} · {l.source}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="hidden text-xs text-muted-foreground sm:inline">Score {l.lead_score}</span>
                  <StatusBadge value={l.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-heading text-base font-semibold">Quick stats</h3>
          <div className="space-y-3">
            {[
              { icon: LifeBuoy, l: "Open tickets", v: k.open_tickets, t: "text-critical bg-critical/10" },
              { icon: Receipt, l: "Collected", v: money(k.collected), t: "text-success bg-success/10" },
              { icon: TrendingUp, l: "Won value", v: money(k.won_value), t: "text-info bg-info/10" },
            ].map((s) => (
              <div key={s.l} className="flex items-center gap-3 rounded-lg border border-border p-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${s.t}`}><s.icon className="h-4 w-4" /></span>
                <div>
                  <p className="text-xs text-muted-foreground">{s.l}</p>
                  <p className="font-heading text-sm font-bold">{s.v}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
